import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../../../api/client.js";
import type { PersonaSummary } from "../../../api/types.js";
import {
  PRESCREEN_STATUS_TEXT, RED_FLAG_TEXT, fmt1, fmtInt, prescreenBlockReasons, readPicks, readShortlistFilters, writePicks, writeShortlistFilters,
  type DrCandidate, type DrJob, type DrRecRow, type DrRecommendation, type DrShortlist, type DrStatus, type V4Bank,
} from "./deep-research-api.js";
import { DrAutoProduceButton } from "./DeepResearchAutoProduce.js";

/**
 * daily-routine Step 2 (shortlist: the skill's SQL, used codes skipped, max 3 per creator, top 15; widen to 4-5 days or a 50K floor as options),
 * Step 3 (the user takes the top 8 to 10 - owner-ordered deviation t408u: 1 to 10, top 8 pre-selected; each source is downloaded and its 20-frame contact sheet goes to the pre-screen agent; the clone-rules
 * engine computes the fits; the table shows 5 + 2 spares, read-only; "a mix of formats" is guidance), the user's 5 (guidance, not a hard limit), and
 * per pick RP or RE through the existing CreateSourceVideo -> RP preselect URL / POST /re/:tenantId/projects. Nothing is produced here.
 */
export function DeepResearchSelection(props: { readonly tenantId: string; readonly base: string; readonly bank: V4Bank; readonly status: DrStatus; readonly csrfToken: string | null; readonly jobs: DrJob[]; readonly onPrescreen: (p: Record<string, unknown>) => Promise<void>; readonly refreshKey?: number }) {
  const { base, bank, status, csrfToken, tenantId } = props;
  const refreshKey = props.refreshKey ?? 0;
  const d = status.defaults;
  const [params, setParams] = useSearchParams();
  // FIX-062 live fix 3: the filters are kept per tenant and bank (V4's default / widen values only).
  const [filters, setFilters] = useState(() => ({ bank, ...readShortlistFilters(tenantId, bank, d) }));
  if (filters.bank !== bank) setFilters({ bank, ...readShortlistFilters(tenantId, bank, d) });
  const { days, minViews, show } = filters;
  const setFilter = (patch: Partial<{ days: number; minViews: number; show: number }>): void => {
    const next = { days, minViews, show, ...patch };
    writeShortlistFilters(tenantId, bank, next);
    setFilters({ bank, ...next });
  };
  const [sl, setSl] = useState<DrShortlist | null>(null);
  const [chosen, setChosen] = useState<number[]>([]);
  const [confirmRefresh, setConfirmRefresh] = useState(false);
  const urlNums = useMemo(() => (params.get("nums") ?? "").split(",").map(Number).filter((n) => Number.isInteger(n) && n > 0), [params]);
  // FIX-062 live fix 2: without ?nums= the table shows the bank's latest pre-screen.
  // t446u/t447u: the latest COMPLETED pre-screen of the bank shown (the server returns only a done job); ?nums= still takes precedence.
  // The answer is kept with its bank, so after a bank switch the other bank's numbers are never shown while the new bank loads.
  // t460u: the server combines all completed pre-screens of the bank from the Israel day of its latest one (`jobs`, `day`); one 5 + 2 over the union.
  const [latest, setLatest] = useState<{ bank: V4Bank; nums: number[]; at: string | null; day: string | null; jobCount: number } | null>(null);
  const latestNums = latest && latest.bank === bank ? latest.nums : [];
  const nums = urlNums.length ? urlNums : latestNums;
  const [rec, setRec] = useState<DrRecommendation | null>(null);
  const [extra, setExtra] = useState("");
  const lastFilterKey = useRef<string | null>(null);

  const loadShortlist = (): void => {
    const key = `${base}|${bank}|${days}|${minViews}|${show}`;
    api.get<DrShortlist>(`${base}/shortlist?bank=${bank}&days=${days}&minViews=${minViews}&show=${show}`).then((r) => {
      setSl(r);
      const pickable = r.rows.filter((x) => x.usa !== null && !x.missingData).map((x) => x.num);
      // FIX-062 live fix 4: a refresh after a job keeps the user's choice (only reels still listed and scored); new filters start from V4's top 8.
      // Owner-ordered deviation from V4 (t408u): still pre-select V4's top 8, or fewer when fewer qualify; the pre-screen runs on 1 to 10.
      if (lastFilterKey.current === key) setChosen((c) => c.filter((n) => pickable.includes(n)));
      else setChosen(pickable.slice(0, d.prescreen.preselect ?? 8));
      lastFilterKey.current = key;
    }).catch(() => setSl(null));
  };
  useEffect(loadShortlist, [base, bank, days, minViews, show, refreshKey]);
  useEffect(() => {
    let live = true;
    api.get<{ nums: number[]; job: DrJob | null; day?: string | null; jobs?: Array<{ id: string }> }>(`${base}/prescreen/latest?bank=${bank}`)
      .then((r) => { if (live) setLatest({ bank, nums: Array.isArray(r?.nums) ? r.nums : [], at: r?.job?.finishedAt ?? r?.job?.createdAt ?? null,
        day: typeof r?.day === "string" ? r.day : null, jobCount: Array.isArray(r?.jobs) && r.jobs.length ? r.jobs.length : r?.job ? 1 : 0 }); })
      .catch(() => { if (live) setLatest(null); });
    return () => { live = false; };
  }, [base, bank, refreshKey]);

  const jobsKey = props.jobs.map((j) => `${j.id}:${j.status}`).join(",");
  useEffect(() => {
    if (!nums.length) { setRec(null); return; }
    let live = true;
    const load = (): void => { api.get<DrRecommendation>(`${base}/recommendation?nums=${nums.join(",")}`).then((r) => { if (live) setRec(r); }).catch(() => undefined); };
    load();
    const t = setInterval(load, 5000);
    return () => { live = false; clearInterval(t); };
  }, [base, nums.join(","), jobsKey]);

  const staleChosen = sl ? sl.rows.filter((r) => chosen.includes(r.num) && !r.linkOk).length : 0;
  const unscoredChosen = sl ? sl.rows.filter((r) => chosen.includes(r.num) && r.usa === null).map((r) => r.num) : [];
  const prescreenRunning = props.jobs.some((j) => j.kind === "prescreen" && (j.status === "queued" || j.status === "running"));
  const blockReasons = prescreenBlockReasons({ isAdmin: status.isAdmin, prescreenConfigured: status.prescreenConfigured, chosen, min: d.prescreen.min, max: d.prescreen.max, unscored: unscoredChosen, running: prescreenRunning });
  const startPrescreen = async (): Promise<void> => {
    await props.onPrescreen({ nums: chosen, confirmRefresh });
    const p = new URLSearchParams(params); p.set("nums", chosen.join(",")); setParams(p, { replace: true });
  };
  const addExtra = (): void => {
    const add = extra.split(/[\s,#]+/).map(Number).filter((n) => Number.isInteger(n) && n > 0);
    if (!add.length) return;
    const p = new URLSearchParams(params); p.set("nums", [...new Set([...nums, ...add])].join(",")); setParams(p, { replace: true }); setExtra("");
  };

  return (
    <>
      <section className="card" data-testid="dr-shortlist">
        <h3>Shortlist <span className="muted">(daily-routine step 2)</span></h3>
        <div className="dr-options">
          <label>Days since the latest pull <select value={days} onChange={(e) => setFilter({ days: Number(e.target.value) })}>{[d.shortlist.days, ...d.widen.days].map((x) => <option key={x} value={x}>{x}{x === d.shortlist.days ? " (default)" : " (widen)"}</option>)}</select></label>
          <label>Min views <select value={minViews} onChange={(e) => setFilter({ minViews: Number(e.target.value) })}>{[d.shortlist.minViews, ...d.widen.minViews].map((x) => <option key={x} value={x}>{fmtInt(x)}{x === d.shortlist.minViews ? " (default)" : " (widen)"}</option>)}</select></label>
          <label>Show <input type="number" min={1} value={show} onChange={(e) => { const v = Number(e.target.value); if (Number.isInteger(v) && v >= 1 && v <= 200) setFilter({ show: v }); }} /></label>
          {days !== d.shortlist.days || minViews !== d.shortlist.minViews || show !== d.shortlist.show
            ? <button type="button" data-testid="dr-shortlist-reset" onClick={() => setFilter({ days: d.shortlist.days, minViews: d.shortlist.minViews, show: d.shortlist.show })}>Reset to V4 defaults</button> : null}
        </div>
        <p className="muted">Rules: reels and videos, 30 s or shorter (length known), US score 70+, highest US score first, then best shares per 1K, nothing already handed off, max 3 per creator. Reels without a US score are shown only when fewer than {d.prescreen.preselect ?? 8} are scored, marked missing data; the pre-screen needs a US score.</p>
        {sl ? <pre className="dr-console">{sl.lines[0]}{sl.usLine ? `\n${sl.usLine}` : ""}</pre> : null}
        {sl && sl.rows.length === 0 ? <p className="muted">Nothing in the window: widen to 4 or 5 days, or drop the floor to 50K.</p> : null}
        {sl && sl.rows.length > 0 ? (
          <table className="table" data-testid="dr-shortlist-table">
            <thead><tr><th>Pre-screen</th><th>#</th><th>account</th><th>views</th><th>sh/1K</th><th>len s</th><th>US</th><th>posted</th><th>link</th><th>video link</th><th>status</th></tr></thead>
            <tbody>
              {sl.rows.map((r) => (
                <tr key={r.code} data-missing={r.usa === null ? "us" : undefined}>
                  <td><input type="checkbox" aria-label={`pre-screen #${r.num}`} disabled={r.usa === null} checked={chosen.includes(r.num)} onChange={(e) => setChosen(e.target.checked ? [...chosen, r.num] : chosen.filter((x) => x !== r.num))} /></td>
                  <td>{r.num}</td><td>@{r.handle}</td><td>{fmtInt(r.plays)}</td><td>{fmt1(r.shares_per_1k ?? 0)}</td><td>{fmt1(r.duration ?? 0)}</td><td>{r.usa === null ? "missing data (run US check)" : r.usa}</td><td>{r.posted}</td>
                  <td><a href={r.link} target="_blank" rel="noopener noreferrer">Watch on IG</a>{r.pinned ? " pinned" : ""}</td>
                  <td>{r.linkOk ? "saved link" : "LINK EXPIRED (refresh = 1 API call)"}</td>
                  <td>{r.prescreen ? PRESCREEN_STATUS_TEXT[r.prescreen] : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        <p>
          Take {d.prescreen.min} to {d.prescreen.max} candidates: the top {d.prescreen.preselect ?? 8} are pre-selected, fewer run when fewer qualify ({chosen.length} chosen).{" "}
          {staleChosen ? <label><input type="checkbox" checked={confirmRefresh} onChange={(e) => setConfirmRefresh(e.target.checked)} /> Refresh {staleChosen} expired link{staleChosen === 1 ? "" : "s"} ({staleChosen} RapidAPI call{staleChosen === 1 ? "" : "s"})</label> : null}{" "}
          <button type="button" disabled={blockReasons.length > 0} title={blockReasons.join(" ")} onClick={() => void startPrescreen()} data-testid="dr-prescreen">Pre-screen</button>
        </p>
        {blockReasons.length ? <p className="muted" data-testid="dr-prescreen-reason">Pre-screen is off: {blockReasons.join(" ")}</p> : null}
      </section>
      <section className="card" data-testid="dr-recommendation">
        <h3>Recommendation <span className="muted">(daily-routine step 3)</span></h3>
        <p className="muted">{rec?.guidance ?? d.recommend.guidance}</p>
        <DrAutoProduceButton tenantId={tenantId} csrfToken={csrfToken} />
        {!urlNums.length && latest && latestNums.length ? <p className="muted" data-testid="dr-rec-source">{prescreenDayLabel(bank, latest, status.timeZone)}: #{latestNums.join(", #")}</p> : null}
        <p>
          <label>Add reels by #number (from the board) <input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="#21270 #21311" /></label>{" "}
          <button type="button" onClick={addExtra}>Add</button>
        </p>
        {rec && nums.length ? <RecommendationTable key={`${bank}|${[...nums].sort((a, b) => a - b).join(",")}`} tenantId={tenantId} base={base} bank={bank} set={[...nums].sort((a, b) => a - b).join(",")} csrfToken={csrfToken} rec={rec} status={status} /> : <p className="muted">Run a pre-screen, or add reels by #number.</p>}
      </section>
    </>
  );
}

/** t460u: "Today's completed pre-screens of the ORIGIN bank (2 jobs, latest 21/10/2026, 18:26:00)" - or the date when the day is not today (Israel time). */
function prescreenDayLabel(bank: V4Bank, l: { at: string | null; day: string | null; jobCount: number }, timeZone?: string): string {
  let today: string | null = null;
  try { today = new Intl.DateTimeFormat("en-CA", { timeZone: timeZone || "Asia/Jerusalem", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date()); } catch { today = null; }
  const when = l.day && l.day === today ? "Today's completed pre-screens" : l.day ? `Completed pre-screens of ${l.day}` : "Completed pre-screens";
  const n = Math.max(1, l.jobCount);
  return `${when} of the ${bank} bank (${n} job${n === 1 ? "" : "s"} combined${l.at ? `, latest ${new Date(l.at).toLocaleString()}` : ""})`;
}

const roleText: Record<DrRecRow["role"], string> = { recommended: "recommended", spare: "spare", "not recommended": "not recommended", "not scored": "not scored" };

function ContactSheet({ c }: { readonly c: DrCandidate }) {
  if (!c.sheetUrl) return null;
  const cols = 5, w = 270, h = 480, n = c.frameTimes?.length ?? 0, rows = Math.ceil(n / cols) || 4;
  return (
    <div style={{ position: "relative", width: "100%", maxWidth: 675, aspectRatio: `${cols * w} / ${rows * h}` }} data-testid={`dr-sheet-${c.num}`}>
      <img src={c.sheetUrl} alt={`contact sheet #${c.num}`} style={{ width: "100%", height: "100%", display: "block" }} />
      {(c.frameTimes ?? []).map((t, i) => (
        <span key={i} style={{ position: "absolute", left: `${((i % cols) * w + 4) / (cols * w) * 100}%`, top: `${(Math.floor(i / cols) * h + 2) / (rows * h) * 100}%`, background: "black", color: "yellow", fontSize: 11, padding: "0 3px" }}>{t.toFixed(1)}s</span>
      ))}
      <p className="muted">dur {c.durationS ?? "-"} · cuts&gt;0.3 [{(c.cutTimes ?? []).join(", ")}]</p>
    </div>
  );
}

function RecommendationTable({ tenantId, base, bank, set, csrfToken, rec, status }: { readonly tenantId: string; readonly base: string; readonly bank: V4Bank; readonly set: string; readonly csrfToken: string | null; readonly rec: DrRecommendation; readonly status: DrStatus }) {
  const navigate = useNavigate();
  // FIX-062 live fix 6: the not-yet-sent picks survive a reload (per tenant and bank).
  const [saved] = useState(() => readPicks(tenantId, bank, set));
  const [picks, setPicksState] = useState<string[]>(saved.picks);
  const [target, setTargetState] = useState<Record<string, "RP" | "RE" | "">>(saved.target);
  const [persona, setPersonaState] = useState<Record<string, string>>(saved.persona);
  const setPicks = (v: string[]): void => { setPicksState(v); writePicks(tenantId, bank, set, { picks: v, target, persona }); };
  const setTarget = (v: Record<string, "RP" | "RE" | "">): void => { setTargetState(v); writePicks(tenantId, bank, set, { picks, target: v, persona }); };
  const setPersona = (v: Record<string, string>): void => { setPersonaState(v); writePicks(tenantId, bank, set, { picks, target, persona: v }); };
  // A pick that was sent is no longer a pending pick (the handoff record shows it from now on).
  const forgetSent = (code: string): void => writePicks(tenantId, bank, set, { picks: picks.filter((x) => x !== code), target, persona });
  const [personas, setPersonas] = useState<PersonaSummary[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<Record<string, string>>({});
  useEffect(() => {
    api.get<{ personas: PersonaSummary[] }>(`/videos/${tenantId}/personas`).then((r) => setPersonas(r.personas.filter((p) => p.active !== false && p.adminStatus !== "suspended"))).catch(() => setPersonas([]));
  }, [tenantId]);
  const cand = new Map(rec.candidates.map((c) => [c.code, c]));
  // FIX-062 follow-up (owner t537u, 2026-10-22): an excluded reel (role "not recommended": red flag, under 4, max 3 per creator, beyond 5 + 2) is not
  // shown at all - not in the table, the contact sheets or the picks. The server's order and roles are unchanged; "not scored" rows (still waiting for
  // the pre-screen, or failed) stay visible with their status.
  // t546u: defensively also any row not known to be 30 s or shorter (the server already leaves them out).
  const rows = rec.rows.filter((r) => r.role !== "not recommended" && r.len !== null && r.len > 0 && r.len <= 30);
  const shownCodes = new Set(rows.map((r) => r.code));
  const handoff = async (r: DrRecRow): Promise<void> => {
    const t = target[r.code];
    if (t !== "RP" && t !== "RE") return;
    const p = persona[r.code] ?? "";
    setBusy(r.code); setMsg((m) => ({ ...m, [r.code]: "Sending the reel's video…" }));
    try {
      const out = await api.post<{ sourceVideoId: string; handoffId: string }>(`${base}/reels/${encodeURIComponent(r.code)}/handoff`, csrfToken, { target: t, ...(p ? { personaId: p } : {}) });
      if (t === "RP") {
        forgetSent(r.code);
        navigate(`/generate/create/video?sourceVideoId=${encodeURIComponent(out.sourceVideoId)}${p ? `&personaId=${encodeURIComponent(p)}` : ""}`);
      } else {
        const created = (await api.post<{ project: { id: string } }>(`/re/${tenantId}/projects`, csrfToken, { personaId: p, sourceVideoId: out.sourceVideoId })).project;
        forgetSent(r.code);
        const linked = await api.post(`${base}/handoffs/${encodeURIComponent(out.handoffId)}/re-project`, csrfToken, { reProjectId: created.id }).then(() => true, () => false);
        if (!linked) {
          // FIX-062 live fix 6: visible, no retry loop (the RE project exists; nothing is created again).
          setMsg((m) => ({ ...m, [r.code]: `The RE project ${created.id} was created but not linked to this reel here. Open it from My RE projects.` }));
          return;
        }
        navigate(`/generate/create/re/projects/${encodeURIComponent(created.id)}`);
      }
    } catch (e) {
      const b = e instanceof ApiError ? (e.body as { error?: string; message?: string } | null) : null;
      setMsg((m) => ({ ...m, [r.code]: b?.message ?? b?.error ?? "The handoff failed." }));
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      <table className="table" data-testid="dr-rec-table">
        <thead><tr><th>pick</th><th>#</th><th>account</th><th>views</th><th>sh/1K</th><th>len</th><th>US</th><th>Unrestricted</th><th>RevEng</th><th>Genjutsu</th><th>tool</th><th>red flags / fix</th><th>one line on the clip</th><th></th></tr></thead>
        <tbody>
          {rows.map((r) => {
            const c = cand.get(r.code);
            return (
              <tr key={r.code} data-role={r.role}>
                <td><input type="checkbox" aria-label={`pick #${r.num}`} checked={picks.includes(r.code)} onChange={(e) => setPicks(e.target.checked ? [...picks, r.code] : picks.filter((x) => x !== r.code))} /></td>
                <td>{r.num}</td><td>@{r.handle}</td><td>{fmtInt(r.views)}</td><td>{fmt1(r.sh1k ?? 0)}</td><td>{fmt1(r.len ?? 0)}</td><td>{r.us === null ? "Not scored" : r.us}</td>
                <td>{r.card ? r.card.unrestricted : "-"}</td><td>{r.card ? r.card.reveng : "-"}</td><td>{r.card ? r.card.genjutsu : "-"}</td>
                <td>{r.card ? `${r.card.tool ?? "drop"} · ${r.card.band}` : c?.status ? PRESCREEN_STATUS_TEXT[c.status] : "not pre-screened"}</td>
                <td>{r.card ? [...r.card.redFlags.map((f) => RED_FLAG_TEXT[f] ?? f), c?.fix ?? ""].filter(Boolean).join(" · ") : c?.error ?? ""}</td>
                <td>{r.oneLine ?? ""}</td>
                <td><strong>{roleText[r.role]}</strong>{r.why ? ` (${r.why})` : ""} · <a href={r.link} target="_blank" rel="noopener noreferrer">Watch on IG</a>
                  {(r.handoffs ?? []).map((h) => (
                    <span key={h.id} data-testid={`dr-sent-${r.num}`}>
                      {" · "}<strong>Sent to {h.target}</strong>
                      {h.target === "RE" ? (h.reProjectId ? <> · project <Link to={`/generate/create/re/projects/${encodeURIComponent(h.reProjectId)}`}>{h.reProjectId}</Link></> : " · project not linked")
                        : h.sourceVideoId ? ` · source video ${h.sourceVideoId}` : ""}
                    </span>
                  ))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="muted">Pick your {status.defaults.recommend.picks} ({picks.length} picked). The estimates come from the pre-screen agent and are shown as they are.</p>
      {rec.candidates.filter((c) => shownCodes.has(c.code) && (c.sheetUrl || c.estimates)).map((c) => (
        <details key={c.code}>
          <summary>#{c.num} contact sheet and estimates · {c.status ? PRESCREEN_STATUS_TEXT[c.status] : ""}</summary>
          <ContactSheet c={c} />
          {c.estimates ? <pre className="dr-console">{JSON.stringify(c.estimates, null, 1)}</pre> : null}
          {(() => { const row = rows.find((x) => x.code === c.code); return row?.card ? <p className="muted">{row.card.routing} {row.card.notes.join(" ")}</p> : null; })()}
        </details>
      ))}
      {picks.some((code) => shownCodes.has(code)) ? <h4>Your picks: RP or RE</h4> : null}
      {picks.map((code) => {
        const r = rows.find((x) => x.code === code);
        if (!r) return null;
        const t = target[code] ?? "";
        const p = persona[code] ?? "";
        return (
          <div key={code} className="card" data-testid={`dr-pick-${r.num}`}>
            #{r.num} @{r.handle}{" "}
            <label><input type="radio" name={`t-${code}`} checked={t === "RP"} onChange={() => setTarget({ ...target, [code]: "RP" })} /> RP</label>{" "}
            <label><input type="radio" name={`t-${code}`} checked={t === "RE"} onChange={() => setTarget({ ...target, [code]: "RE" })} /> RE</label>{" "}
            <select aria-label="persona" value={p} onChange={(e) => setPersona({ ...persona, [code]: e.target.value })}>
              <option value="">{t === "RE" ? "Choose a persona (required)" : "Persona (optional)"}</option>
              {personas.map((x) => <option key={x.personaId} value={x.personaId}>{x.displayName}</option>)}
            </select>{" "}
            <button type="button" disabled={busy !== null || !status.handoffConfigured || (t !== "RP" && t !== "RE") || (t === "RE" && !p)} onClick={() => void handoff(r)}>
              {t === "RE" ? "Send to RE" : t === "RP" ? "Send to RP" : "Choose RP or RE"}
            </button>
            {msg[code] ? <span className="muted"> {msg[code]}</span> : null}
          </div>
        );
      })}
    </>
  );
}
